import React, { useCallback, useRef } from "react";
import { Space, Tag, Tooltip, Typography } from "antd";
import { FileTextOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Button, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import apiService from "../../Utilities/APIService";

const { Text } = Typography;

const STATUS_COLOR = {
  Paid: "success",
  Pending: "warning",
  Overdue: "error",
  Draft: "default",
};

/** The client's own invoices, read-only. */
const PortalInvoices = () => {
  const { mutate } = useMutations();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      let result = { data: [], total: 0 };
      await mutate("portalInvoices", {
        method: "get",
        endpoint: "/portal/invoices",
        params: { page, pageSize, search },
        onSuccess: (res) => {
          result = { data: res?.items || [], total: res?.totalCount || 0 };
        },
        onError: () => {
          result = { data: [], total: 0 };
        },
      });
      return result;
    },
    [mutate]
  );

  // The endpoint returns rendered HTML, not JSON. It goes through axiosClient so the
  // bearer token and the OTP header the API requires are attached like everywhere else.
  // Opening it is recorded server-side; printing is recorded here, because only the
  // browser knows the client actually reached for a printer.
  const openInvoice = async (invoiceId, print = false) => {
    try {
      const response = await apiService.get(
        `/portal/invoices/${invoiceId}/html`,
        {},
        { responseType: "text" }
      );
      const win = window.open("", "_blank");
      if (win) {
        win.document.write(response?.data ?? "");
        win.document.close();
        if (print) win.print();
      }

      if (print) {
        await mutate("portalInvoicePrinted", {
          method: "post",
          endpoint: `/portal/invoices/${invoiceId}/printed`,
        });
      }
    } catch {
      // handleAxiosError in the interceptor already surfaced the failure.
    }
  };

  const columns = [
    { title: "Invoice", dataIndex: "invoiceNumber", key: "invoiceNumber" },
    {
      title: "Issued",
      dataIndex: "issuedAt",
      key: "issuedAt",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Due",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—"),
      responsive: ["md"],
    },
    { title: "Package", dataIndex: "packageName", key: "packageName", responsive: ["lg"] },
    {
      title: "Total",
      key: "total",
      render: (_, r) => (
        <Text strong>
          {Number(r.totalAmount ?? 0).toFixed(2)} {r.currencyCode}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag>,
    },
    {
      title: "",
      key: "actions",
      render: (_, r) => (
        <Space size={0}>
          <Tooltip title="Open invoice">
            <Button
              variant="ghost"
              icon={<FileTextOutlined />}
              onClick={() => openInvoice(r.invoiceId)}
            />
          </Tooltip>
          <Tooltip title="Print invoice">
            <Button
              variant="ghost"
              icon={<PrinterOutlined />}
              onClick={() => openInvoice(r.invoiceId, true)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Your billing history and downloadable invoices."
      />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="invoiceId"
      />
    </div>
  );
};

export default PortalInvoices;
